import MuiBox from '@mui/material/Box';
import MuiList from '@mui/material/List';
import MuiListItem from '@mui/material/ListItem';
import MuiListItemText from '@mui/material/ListItemText';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { visuallyHiddenSx } from './Box';

/**
 * List and table primitives, built on Material UI.
 *
 * Both are driven by data rather than by children carrying raw list items or
 * cells, which is what keeps that markup out of pages. The trade is a little
 * more ceremony at the call site — cells come from a `render` function — in
 * exchange for a table whose header and body cannot drift apart.
 */

/** An unordered or ordered list. `items` may be strings or React nodes. */
export function List({ items = [], ordered = false, dense = true, ...rest }) {
  return (
    <MuiList component={ordered ? 'ol' : 'ul'} dense={dense} disablePadding {...rest}>
      {items.map((item, index) => (
        <MuiListItem key={item?.key ?? index} disableGutters>
          <MuiListItemText primary={item?.content ?? item} />
        </MuiListItem>
      ))}
    </MuiList>
  );
}

/**
 * A label/value list, for the "field: value" blocks on detail screens.
 *
 * Rendered as a real <dl>. That pattern *is* a description list — a stack of
 * rows with bold text conveys the relationship visually and not at all
 * otherwise.
 */
export function DescriptionList({ items = [], ...rest }) {
  return (
    <MuiBox component="dl" sx={{ m: 0, display: 'flex', flexDirection: 'column', gap: 2 }} {...rest}>
      {items.map((item) => (
        <MuiBox
          key={item.term}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'space-between',
            alignItems: 'baseline'
          }}
        >
          <Typography component="dt" variant="body2" color="text.secondary">
            {item.term}
          </Typography>
          <Typography component="dd" variant="body1" sx={{ m: 0, fontWeight: 600 }}>
            {item.detail}
          </Typography>
        </MuiBox>
      ))}
    </MuiBox>
  );
}

/**
 * A data table.
 *
 * `columns` is [{ key, header, render? }] and `rows` is the data. `caption` is
 * required: it names the table for anyone navigating by landmark, and is
 * visually hidden unless `showCaption` is set.
 *
 * TableContainer scrolls horizontally on its own, so a wide table never makes
 * the whole page scroll sideways.
 */
export function Table({
  caption,
  showCaption = false,
  columns = [],
  rows = [],
  rowKey,
  emptyMessage,
  ...rest
}) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <MuiTable size="small" {...rest}>
        <caption style={showCaption ? undefined : visuallyHiddenSx}>{caption}</caption>

        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.key} component="th" scope="col">
                {column.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => (
              <TableRow key={rowKey ? rowKey(row, index) : index} hover>
                {columns.map((column) => (
                  <TableCell key={column.key}>
                    {column.render ? column.render(row, index) : row[column.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
}
